import React, { useContext, useState } from 'react';
import { MachineContext } from '../DataContexts/MachineContext';
import './CreateAccountForm.css';

// Template values pre-filling the form; every field stays editable.
const TEMPLATE_VALUES = {
  name: 'TBD',
  type: 'TBD',
  province: 'TBD',
  city: 'TBD',
  street: 'TBD',
  postalcode: '0000',
  housenumber: '1',
  maxTracksOnMachine: '4750',
  amountTokens: '20',
  overeenkomst: 'UNASSIGNED',
};

const OVEREENKOMST_LABELS = {
  UNASSIGNED: 'Unassigned',
  BASIC: 'Basic',
  PRO: 'Pro',
  UNLIMITED: 'Unlimited',
};

const TEXT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'type', label: 'Type' },
  { key: 'province', label: 'Province' },
  { key: 'city', label: 'City' },
  { key: 'street', label: 'Street' },
  { key: 'postalcode', label: 'Postal code' },
  { key: 'housenumber', label: 'House number' },
];

const NUMBER_FIELDS = [
  { key: 'maxTracksOnMachine', label: 'Max tracks on machine' },
  { key: 'amountTokens', label: 'Amount of tokens' },
];

const CreateAccountForm = () => {
  const { createMachine } = useContext(MachineContext);
  const [form, setForm] = useState(TEMPLATE_VALUES);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [createdMachineId, setCreatedMachineId] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = form.name.trim();
    if (!name || name.toUpperCase() === 'TBD') {
      setFormError('Please enter a machine name (it may not be empty or "TBD").');
      return;
    }

    const maxTracksOnMachine = parseInt(form.maxTracksOnMachine, 10);
    const amountTokens = parseInt(form.amountTokens, 10);
    if (Number.isNaN(maxTracksOnMachine) || Number.isNaN(amountTokens)) {
      setFormError('Max tracks on machine and amount of tokens must be numbers.');
      return;
    }

    setFormError(null);
    setCreatedMachineId(null);
    setCopied(false);
    setSubmitting(true);
    try {
      const createdMachine = await createMachine({
        name,
        type: form.type,
        province: form.province,
        city: form.city,
        street: form.street,
        postalcode: form.postalcode,
        housenumber: form.housenumber,
        maxTracksOnMachine,
        amountTokens,
        overeenkomst: form.overeenkomst,
      });
      setCreatedMachineId(createdMachine?.id ?? null);
      setForm(TEMPLATE_VALUES);
    } catch (error) {
      setFormError(`Failed to create account: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyId = () => {
    if (!createdMachineId || !navigator.clipboard) return;
    navigator.clipboard.writeText(createdMachineId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <form className="create-account-form" onSubmit={handleSubmit}>
      <div className="create-account-fields">
        {TEXT_FIELDS.map(({ key, label }) => (
          <div className="create-account-field" key={key}>
            <label htmlFor={`create-account-${key}`}>{label}:</label>
            <input
              id={`create-account-${key}`}
              type="text"
              value={form[key]}
              onChange={(e) => handleFieldChange(key, e.target.value)}
            />
          </div>
        ))}
        {NUMBER_FIELDS.map(({ key, label }) => (
          <div className="create-account-field" key={key}>
            <label htmlFor={`create-account-${key}`}>{label}:</label>
            <input
              id={`create-account-${key}`}
              type="number"
              value={form[key]}
              onChange={(e) => handleFieldChange(key, e.target.value)}
            />
          </div>
        ))}
        <div className="create-account-field">
          {/* DJB-183: label renamed to Plan; the overeenkomst field/values stay */}
          <label htmlFor="create-account-overeenkomst">Plan:</label>
          <select
            id="create-account-overeenkomst"
            value={form.overeenkomst}
            onChange={(e) => handleFieldChange('overeenkomst', e.target.value)}
          >
            {Object.entries(OVEREENKOMST_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="create-account-actions">
        <button type="submit" className="create-account-submit" disabled={submitting}>
          {submitting ? 'Creating...' : 'Create Account'}
        </button>
      </div>

      {formError && (
        <p className="create-account-error" role="alert">{formError}</p>
      )}

      {createdMachineId && (
        <div className="create-account-success" role="status">
          <span>Account created. New machine id:</span>
          <code className="create-account-id">{createdMachineId}</code>
          <button type="button" className="create-account-copy" onClick={handleCopyId}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}
    </form>
  );
};

export default CreateAccountForm;
